import React, { useState, useEffect, useRef, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import {
  useGetGradeDetailsQuery,
  useUpdateMarksForStudentMutation,
} from '@school_ify/schoolify-repository/src/teacher/types';

import { calculateNodeOffset, randString, convertFileNameToMime } from 'clean/utils/functions';
import Paper from 'clean/components/common/Paper/Paper';
import EntryTitle from 'clean/components/common/EntryTitle/EntryTitle';
import TextField from 'clean/components/common/TextField/TextField';
import Select from 'clean/components/common/Select/Select';
import FileUploader from 'clean/components/common/FileUploader/FileUploader';
import Button from 'clean/components/common/Button/Button';

import './Details.scoped.scss';

interface Props {
  assignmentId: string;
  studentId: string;
  marks: any;
  gradeId: string;
  updateGradesData: (arg: any) => void;
  rootNode: React.RefObject<HTMLElement>;
  onClose: () => void;
}

const Details: React.FC<Props> = ({
  rootNode,
  assignmentId,
  studentId,
  gradeId,
  marks,
  updateGradesData,
  onClose,
}) => {
  const topOffset = 90;
  const detailsNode = useRef(null);
  const { t } = useTranslation();
  const [id, setId] = useState(null);
  const [date, setDate] = useState('');
  const [offset, setOffset] = useState({ x: null, y: null });
  const [title, setTitle] = useState('');
  const [teacher, setTeacher] = useState(null);
  const [types, setTypes] = useState([]);
  const [type, setType] = useState(null);
  const [grade, setGrade] = useState(gradeId);
  const [files, setFiles] = useState([]);
  const [comment, setComment] = useState('');
  const gradeOptions = useMemo(
    () => marks.map(({ value, label }) => ({ id: value, label })),
    [marks]
  );

  const [updateMarksForStudentMutation, updateData] = useUpdateMarksForStudentMutation();
  const data = useGetGradeDetailsQuery({
    variables: {
      assignment_id: assignmentId,
      student_id: studentId,
    },
  });

  const updateOffset = () => {
    if (detailsNode.current) {
      const nodeOffsets = calculateNodeOffset(rootNode.current);
      let top = nodeOffsets.y - topOffset;
      let left = nodeOffsets.x + rootNode.current.offsetWidth - 1; // -1 is so the border doesn't glitch out when running non-native resolution

      // Means that there is a collision on right side, and we should move it back to the left side
      if (window.innerWidth - left < detailsNode.current.offsetWidth) {
        left -= detailsNode.current.offsetWidth + rootNode.current.offsetWidth - 1; // The same reason
      }

      if (window.innerHeight - top < detailsNode.current.offsetHeight) {
        top -= detailsNode.current.offsetHeight - (window.innerHeight - top) + 10;
      }

      setOffset({ x: left, y: top });
    }
  };

  const save = () => {
    updateMarksForStudentMutation({
      variables: {
        id: { _eq: id },
        mark_id: grade,
        comment,
      },
    });
  };

  useEffect(() => {
    if (updateData.data) {
      updateGradesData(updateData.data.update_assignment_user.returning[0]);
      onClose();
    }
  }, [updateData.data]);

  useEffect(() => {
    if (data?.data?.assignments[0]) {
      const assignment = data.data.assignments[0];
      const idForType = randString();

      setFiles(
        assignment.assignment_files.map(({ id, path, title }) => {
          return { id, path, name: title, mimeType: convertFileNameToMime(path) };
        })
      );
      setDate(DateTime.fromISO(assignment.created_at).toFormat('MM.dd.y'));
      setTypes([{ id: idForType, label: assignment.assignment_type.name }]);
      setType(idForType);
      setTitle(assignment.title);
      setTeacher(assignment?.user?.full_name);
      setComment(assignment?.assignment_users[0].comment);
      setId(assignment?.assignment_users[0].id);
    }
  }, [data?.data?.assignments]);

  useEffect(updateOffset, [data.loading, detailsNode.current]);

  if (data.loading) {
    return null;
  }

  return (
    <div
      ref={detailsNode}
      style={{ '--offset-x': offset.x, '--offset-y': offset.y } as React.CSSProperties}
      className="grade-details-wrapper"
    >
      <div
        onClick={e => {
          e.stopPropagation();
          onClose();
        }}
        className="backdrop-overlay"
      ></div>
      <div className="wrapper">
        <Paper>
          <div className="content">
            <div className="title">{title}</div>

            <div
              className="close"
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            />

            <div className="entry">
              <EntryTitle title={t('common:Date')}>
                {
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  <TextField disabled value={date} onChange={() => {}} />
                }
              </EntryTitle>
            </div>

            <div className="entry">
              <EntryTitle title={t('common:Type')}>
                <Select
                  maxHeight={200}
                  disabled
                  options={types}
                  value={type}
                  onChange={value => {
                    setType(value);
                  }}
                />
              </EntryTitle>
            </div>

            <div className="entry">
              <EntryTitle title={t('common:Teacher')}>
                <TextField disabled value={teacher} onChange={() => null} />
              </EntryTitle>

              <div className="grade">
                <EntryTitle title={t('common:Mark')}>
                  <Select
                    maxHeight={200}
                    options={gradeOptions}
                    value={grade}
                    onChange={value => {
                      setGrade(value);
                    }}
                  />
                </EntryTitle>
              </div>
            </div>

            {files.length > 0 && (
              <div className="entry">
                <FileUploader
                  readOnly
                  fileListTitle={t('grades:Submition')}
                  files={files}
                  setFiles={setFiles}
                />
              </div>
            )}

            <div className="entry">
              <EntryTitle title={t('common:Comment')}>
                <TextField
                  multiLine
                  value={comment}
                  onChange={e => {
                    setComment(e.target.value);
                  }}
                />
              </EntryTitle>
            </div>

            <div className="entry button">
              <Button
                onClick={save}
                label={t('common:save')}
                color="alternative-primary"
                variant="filled"
              />
            </div>
          </div>
        </Paper>
      </div>
    </div>
  );
};

export default Details;
